// Generator för tjänst × ort inom foto (t.ex. /foto/foretagsfotograf-stockholm).
// Data ligger i src/content/foto-service-cities.ts. Varje sida får unik
// metadata, LocalBusiness-, Service-, FAQ-, ImageGallery-, Person- och
// AEO/GEO-schema samt lokala internlänkar.

import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { LocalMobileGallery } from "@/components/site/landing/LocalMobileGallery";
import { FotoQuoteForm } from "@/components/site/foto/FotoQuoteForm";
import { PhotoMasonry } from "@/components/site/foto/PhotoMasonry";
import { coverPhotoFor, galleryPhotos, photosBy } from "@/content/photo-gallery";
import { PHOTOGRAPHER } from "@/content/foto";
import { fotoServiceCities, fotoServiceCitiesFor } from "@/content/foto-service-cities";
import {
  aeoGeo,
  breadcrumbs,
  faqPage,
  imageGallery,
  person,
  photoBusiness,
  serviceSchema,
  speakable,
} from "@/lib/seo/schema";

export function fotoServiceCityRoute(slug: string) {
  const page = fotoServiceCities.find((p) => p.slug === slug);
  if (!page) throw new Error(`Unknown foto service city ${slug}`);
  const PATH = page.path;
  const URL = `https://fenny.se${PATH}`;
  const pool = page.gallery === "alla" ? galleryPhotos : photosBy(page.gallery);
  const photos = (pool.length >= 9 ? pool : galleryPhotos).slice(0, 12);
  const cover = coverPhotoFor(page.slug, page.gallery === "alla" ? undefined : page.gallery);
  const FAQ: LandingFAQ[] = page.faq.map((f) => ({ q: f.q, a: f.a }));
  const siblings = fotoServiceCitiesFor(page.citySlug).filter((p) => p.slug !== page.slug);
  const sameService = fotoServiceCities.filter(
    (p) => p.serviceKey === page.serviceKey && p.slug !== page.slug,
  );

  return {
    head: () => ({
      meta: [
        { title: page.metaTitle },
        { name: "description", content: page.description },
        { property: "og:title", content: page.metaTitle },
        { property: "og:description", content: page.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: URL },
        { name: "geo.region", content: "SE" },
        { name: "geo.placename", content: page.city },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover.src.startsWith("/")
          ? [
              { property: "og:image", content: `https://fenny.se${cover.src}` },
              { name: "twitter:image", content: `https://fenny.se${cover.src}` },
            ]
          : []),

      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            aeoGeo({
              path: PATH,
              name: page.metaTitle,
              description: page.description,
              question: page.question,
              answer: page.quickAnswer,
              speakableSelectors: ["[data-quick-answer]"],
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            photoBusiness({
              city: page.city,
              path: PATH,
              description: page.description,
              region: page.region,
              images: photos,
              email: "hey@fenny.se",
              contactName: PHOTOGRAPHER.name,
              ...(page.citySlug === "stockholm"
                ? { bookingPath: "/foto/boka-fotograf-stockholm" }
                : {}),

            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: page.crumb,
              path: PATH,
              serviceType: page.serviceType,
              description: page.description,
              areaServed: [page.city, page.region],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            imageGallery({
              name: `${page.crumb} — Fenny Foto`,
              path: PATH,
              description: page.galleryHeading,
              images: photos,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            person({
              name: PHOTOGRAPHER.name,
              jobTitle: PHOTOGRAPHER.role,
              email: "hey@fenny.se",
              path: PATH,
              description: PHOTOGRAPHER.bio,
              knowsAbout: page.knowsAbout,
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: "Hem", path: "/" },
              { name: "Foto", path: "/foto" },
              { name: `Fotograf ${page.city}`, path: `/foto/${page.citySlug}` },
              { name: page.crumb, path: PATH },
            ]),
          ),
        },
      ],
    }),
    component: function FotoServiceCityPage() {
      const link = "underline decoration-mint/40 underline-offset-4 hover:text-mint";
      return (
        <LandingPage
          heroImage={{ src: cover.src, alt: `${page.eyebrow} — ${cover.alt}` }}
          eyebrow={page.eyebrow}
          title={page.h1}
          lede={page.lede}
          tldr={<p>{page.quickAnswer}</p>}
          ctaPrimary={{ label: "Begär offert", to: "#offert" }}
          ctaSecondary={{ label: `Fotograf ${page.city}`, to: `/foto/${page.citySlug}` }}
          trackingId={`foto_${page.slug}`}
          stills={false}
          videos={false}
          mobileCollapseAfter={2}
          afterHero={
            <>
              <LocalMobileGallery
                photos={photos.slice(0, 6)}
                label={`Foto · ${page.city}`}
                ctaTo="#offert"
                ctaLabel="Begär offert"
                heading={page.galleryHeading}
              />
              <div className="hidden py-12 md:block">
                <PhotoMasonry
                  photos={photos}
                  filterable={false}
                  initial={9}
                  eyebrow="Portfolio"
                  heading={page.galleryHeading}
                />
              </div>
            </>
          }
          bullets={page.bullets}
          sections={[
            {
              id: "lokal-vinkel",
              heading: page.localAngle.heading,
              body: (
                <>
                  {page.localAngle.paragraphs.map((p, i) => (
                    <p key={i} className={i > 0 ? "mt-4" : undefined}>
                      {p}
                    </p>
                  ))}
                </>
              ),
            },
            {
              id: "upplagg",
              heading: page.logistics.heading,
              body: (
                <>
                  {page.logistics.paragraphs.map((p, i) => (
                    <p key={i} className={i > 0 ? "mt-4" : undefined}>
                      {p}
                    </p>
                  ))}
                </>
              ),
            },
            {
              id: "mer-i-staden",
              heading: `Mer foto i ${page.city}`,
              body: (
                <p>
                  <a className={link} href={`/foto/${page.citySlug}`}>
                    Fotograf {page.city}
                  </a>{" "}
                  är översiktssidan för orten.{" "}
                  {siblings.map((s, i) => (
                    <span key={s.slug}>
                      {i > 0 ? " · " : ""}
                      <a className={link} href={s.path}>
                        {s.nav}
                      </a>
                    </span>
                  ))}
                  .
                </p>
              ),
            },
            {
              id: "andra-orter",
              heading: "Samma tjänst på andra orter",
              body: (
                <p>
                  {sameService.map((s, i) => (
                    <span key={s.slug}>
                      {i > 0 ? " · " : ""}
                      <a className={link} href={s.path}>
                        {s.nav}
                      </a>
                    </span>
                  ))}
                  . Priserna följer{" "}
                  <a className={link} href="/branschstudier/bildindex-2026">
                    Bildindex 2026
                  </a>
                  .
                </p>
              ),
            },
            {
              id: "las-vidare",
              heading: "Läs vidare",
              body: (
                <p>
                  {page.related.map((r, i) => (
                    <span key={r.to}>
                      {i > 0 ? " · " : ""}
                      <a className={link} href={r.to}>
                        {r.label}
                      </a>
                    </span>
                  ))}
                </p>
              ),
            },
          ]}
          faq={FAQ}
          riskReducers
          closing={{
            title: `Ska vi fotografera i ${page.city}?`,
            body: `Beskriv uppdraget så återkommer ${PHOTOGRAPHER.name} med upplägg och fast pris inom en arbetsdag.`,
            primary: { label: "Begär offert", to: "#offert" },
          }}
          contactSlot={<FotoQuoteForm location={`foto_${page.slug}`} defaultCity={page.city} />}
        />
      );
    },
  };
}
